// status-operating-table-snapshot.ts
//
// Split out of status-renderer.ts (D12, phase 05 B1 — that file sat at the
// 200-line ceiling). Owns `StatusPayloadWithCost`/`WithOperatingTable` and
// the operating-table I/O (tickets/attempt-state/gates/quota-sleep reads);
// status-renderer.ts imports these back and keeps the read-only snapshot
// orchestration + the base `StatusPayload`/`ReadableMissionStore` shapes.
import { join } from "node:path";
import { readGateFile } from "./gate-registry.js";
import {
  type CostAttemptLimitsConfig,
  computeMissionCost,
  type MissionCostSummary,
} from "./mission-cost-accounting.js";
import { readPauseResumeState } from "./pause-resume-state.js";
import { readQuotaSleepState } from "./quota-sleep-scheduler.js";
import { readMaxCountedFailures } from "./status-effective-config-readers.js";
import {
  buildMissionOperatingSummary,
  buildTicketOperatingRows,
  type MissionOperatingSummary,
  type TicketOperatingRow,
} from "./status-operating-table.js";
import {
  buildStatusPayload,
  type ReadableMissionStore,
  type StatusPayload,
} from "./status-payload-types.js";
import { readAttemptStateFile } from "./supervisor-attempt-state.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";

/** P13 Agent B: cost/limits numbers — separate type so Agent C's renderStatusHtml table-row tests keep using bare StatusPayload literals. */
export interface StatusPayloadWithCost extends StatusPayload {
  cost: MissionCostSummary;
  limits: CostAttemptLimitsConfig;
}

/**
 * buildStatusPayload + mission cost summary; `limits` injectable for tests.
 *
 * Req 7 (phase 05): `status` reports `"paused"` when `pause-state.json` has
 * `paused:true` — that label deliberately does NOT live in the journal/
 * mission-state projection (H3: pause/resume journal events fall through
 * the reducer's `default`, never touching `state.status`), so it is
 * overlaid here, at read time, from the one file that IS the source of
 * truth for pause (pause-resume-state.ts). The projected label underneath
 * is untouched — resume simply stops this override from firing.
 */
export async function buildStatusPayloadWithCost(
  store: ReadableMissionStore,
  limits: CostAttemptLimitsConfig,
): Promise<StatusPayloadWithCost> {
  const [cost, pauseState] = await Promise.all([
    computeMissionCost(store.controlRoot),
    readPauseResumeState(store.controlRoot),
  ]);
  const payload = buildStatusPayload(store);
  return {
    ...payload,
    status: pauseState.paused ? "paused" : payload.status,
    cost,
    limits,
  };
}

export interface StatusPayloadWithOperatingTable extends StatusPayloadWithCost {
  tickets_detail: TicketOperatingRow[];
  mission_summary: MissionOperatingSummary;
}

/**
 * Adds the operating-table rows to an already-built cost payload.
 * `missionWorkspace` is the execution repo (`<mission_home>/workspace`) that
 * owns `.avatar/tickets/*.json` — best-effort throughout: a mission that
 * never reached ticket-seeding (pre-bootstrap) or has no gates/quota state
 * yet renders an empty table, never throws.
 */
export async function buildStatusPayloadWithOperatingTable(
  payload: StatusPayloadWithCost,
  controlRoot: string,
  missionWorkspace: string,
): Promise<StatusPayloadWithOperatingTable> {
  const [tickets, attemptState, gates, quotaState, maxCountedFailures] = await Promise.all([
    loadTicketsFromRepo(missionWorkspace).catch(() => []),
    readAttemptStateFile(controlRoot),
    readGateFile(controlRoot).catch(() => undefined),
    readQuotaSleepState(join(controlRoot, "quota-sleep.json")).catch(() => undefined),
    readMaxCountedFailures(controlRoot),
  ]);
  return {
    ...payload,
    tickets_detail: buildTicketOperatingRows(
      tickets,
      payload.tickets,
      attemptState,
      payload.ticket_last_verdict,
      maxCountedFailures,
    ),
    mission_summary: buildMissionOperatingSummary(
      payload.mission_id,
      payload.status,
      gates,
      quotaState,
      payload.cost.attempts_without_usage,
    ),
  };
}
